import { ReactNode, useMemo } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { useSellerAccountContext } from 'src/contexts/SellerAccountContext';
import {
  PosMultiSelect,
  PosMultiSelectProps,
} from 'src/core/POS/PosMultiSelect';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { TeamMembersIcon } from 'src/svgs/TeamMembersIcon';
import { EMPTY_GUID } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature } from 'src/WebApiController';

export const SellerAccountEmployeeMultiSelector = ({
  placeholderText,
  ...props
}: Omit<PosMultiSelectProps, 'valueOptionsContent'> & {
  showEmail?: boolean;
  allowUnchanged?: boolean;
}) => {
  const { allActiveUserInfos } = useSellerAccountContext();

  const unchanged = useContent(ContentId.DontChange);

  const hasUserTeams = useUserHasFeature(Feature.UserTeams);

  const teamIds = useMemo(() => {
    if (!hasUserTeams || !allActiveUserInfos) return new Set<string>();
    return new Set(
      Object.entries(allActiveUserInfos)
        .filter(([_, info]) => info.isTeam)
        .map(([id]) => id)
    );
  }, [hasUserTeams, allActiveUserInfos]);

  const allActiveUserOptions = useMemo(() => {
    if (!allActiveUserInfos) return {};

    const results = {} as Record<string, string>;
    if (props.allowUnchanged) {
      results[EMPTY_GUID] = unchanged;
    }

    Object.values(allActiveUserInfos).reduce((r, cur) => {
      r[cur.userId] = props.showEmail
        ? `${cur.displayName} (${cur.email})`
        : cur.displayName;
      return r;
    }, results);

    return results;
  }, [allActiveUserInfos, props.allowUnchanged, props.showEmail, unchanged]);

  const valueOptionsPostfixIcons = useMemo(() => {
    if (!allActiveUserOptions || teamIds.size === 0) return {};

    const results = {} as Record<string, ReactNode>;
    for (const sellerUserId of Object.keys(allActiveUserOptions)) {
      if (teamIds.has(sellerUserId)) {
        results[sellerUserId] = <TeamMembersIcon />;
      }
    }
    return results;
  }, [allActiveUserOptions, teamIds]);

  return (
    <PosMultiSelect
      {...props}
      loading={allActiveUserInfos == null}
      placeholderText={placeholderText ?? ContentId.Everyone}
      valueOptionsContent={allActiveUserOptions}
      valueOptionsPostfixIcon={valueOptionsPostfixIcons}
    />
  );
};
